/**
 * Page guide for /bankStatements — pure data, no UI imports (see help/types.ts).
 * Every selector names a `data-help` attribute rendered under pages/BankStatements/ or passed
 * to a shared component through `helpAnchor`.
 */
import type { PageHelp } from 'help/types';

export const bankStatementsHelp: PageHelp = {
  title: 'Bank statements',
  intro:
    'This is where incoming money enters PCT: import the bank’s CAMT file, make sure every entry has a payer (the client the money belongs to), then clear it against that client’s open premium. Cleared money becomes payments that continue on the Payments page towards the declaration to insurers; any amount not matched to premium goes to the client balance.',
  sections: [
    'Getting around',
    'Finding a statement',
    'Adding a statement',
    'Importing a CAMT file',
    'Reading the statement list',
    'Statement actions',
    'Statement entries',
    'Clearing perfect matches',
    'Deleting a statement',
    'Clearing an entry',
    'Clearing to client balance',
    'Deleting an entry',
    'Clearance information',
  ],
  contexts: {
    'add-menu': 'Click "Add bank statement" on the second toolbar line.',
    'import-dialog': 'Click "Add bank statement" and choose "Import from XML file".',
    'statement-selected':
      'Tick the checkbox at the start of a statement row; the extra buttons appear in that row’s Options column.',
    'statement-editing':
      'Choose "Enter manually" under "Add bank statement", or change a cell of a manually entered statement.',
    'entries-shown': 'Tick a statement in the upper list; its entries appear in the lower list.',
    'entry-editing':
      'Change the Payer cell of an entry, or use "Suggest client resolvement" on a statement or an entry.',
    'perfect-match-dialog':
      'Tick a statement and click "Clear all perfect matches" (the circling-arrows icon) in its Options column.',
    'delete-dialog': 'Click "Delete bank statement" (the bin icon) in a statement’s Options column.',
    'clear-dialog':
      'Tick a statement, then click "Clear bank statement entry" on an entry that has a payer and a positive amount.',
    'saldo-dialog':
      'Tick "Include negative amounts", then click "Clear bank statement entry" on an entry that has a payer and a negative amount.',
    'delete-entry-dialog':
      'Click "Delete bank statement entry" (the bin icon) on an entry of a manually entered statement.',
    'info-dialog': 'Click "Clearance information" (the i icon) on a cleared entry.',
  },
  tips: [
    'Imported statements are read-only: their cells cannot be edited and their entries cannot be deleted one by one — only the Payer can be set. If a file was wrong, delete the whole statement (with a comment) and import it again.',
    'During import PCT recognises payments from GrECo’s own collection account, from insurer accounts, from GrECo commission accounts and bank fees. Outgoing payments to insurers, GrECo commission and bank fees are marked cleared straight away.',
    'Payer suggestions look, in this order, at the client number before the first "-" in the payment reference, at the payer’s company or personal code and at the payer’s name. If the reference and the code point to different clients, nothing is suggested.',
    'Entries highlighted in the lower list were cleared automatically by "Clear all perfect matches".',
    'GrECo Latvia: an entry whose payment reference names an imported QTG invoice is cleared by "Clear all perfect matches" when the amount equals the invoice premium and every invoice entry has its installment; otherwise the "Pending QTG invoice" column shows the invoice until the policy is in GOS and the entry is cleared by hand, where the clearance lists the invoice installments first. The QTG invoice columns are shown only to users with access to a Latvian GrECo company.',
    'The company selection at the top right is shared with the other PCT pages and decides which statements are loaded.',
  ],
  steps: [
    // ----------------------------------------------------------------- Getting around
    {
      selector: '[data-help="bank-statements-page-switcher"]',
      title: 'Switch to another PCT page',
      body: 'The icons jump between the PCT pages: Bank statements, Insurer reports, Client balance, Payment plans, Reminders and Payments. Hover an icon to see its name; the highlighted one is the page you are on.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pereiti į kitą PCT puslapį',
          body: 'Piktogramos veda į PCT puslapius: „Banko išrašai“, „Draudiko ataskaitos“, „Kliento balansas“, „Mokėjimų planas“, „Priminimai“ ir „Mokėjimai“. Užveskite pelę ant piktogramos, kad pamatytumėte pavadinimą; pažymėta piktograma rodo puslapį, kuriame esate.',
        },
        lv: {
          title: 'Pāriet uz citu PCT lapu',
          body: 'Ikonas ved uz PCT lapām: “Bankas konta izraksti”, “Apdrošinātāja atskaites”, “Client balance”, “Payment plans”, “Reminders” un “Payments”. Novietojiet peli virs ikonas, lai redzētu nosaukumu; iezīmētā ikona ir lapa, kurā atrodaties.',
        },
      },
    },
    {
      selector: '[data-help="app-user-manual"]',
      title: 'Download the user manual',
      body: 'Downloads the complete PCT user manual as a PDF, with screenshots of every page. You get it in the language selected in Settings: Lithuanian or Latvian when the app is in that language, English otherwise. The page guide offers the same download (the book next to EN / LT / LV) in the guide’s language.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atsisiųsti naudotojo vadovą',
          body: 'Atsisiunčia visą PCT naudotojo vadovą PDF formatu su visų puslapių ekrano kopijomis. Gaunate jį kalba, pasirinkta „Nustatymuose“: lietuvių arba latvių, kai programa veikia ta kalba, kitu atveju – anglų. Puslapio vadove tas pats atsisiuntimas (knygos piktograma šalia EN / LT / LV) pateikiamas vadovo kalba.',
        },
        lv: {
          title: 'Lejupielādēt lietotāja rokasgrāmatu',
          body: 'Lejupielādē pilnu PCT lietotāja rokasgrāmatu PDF formātā ar visu lapu ekrānuzņēmumiem. Jūs to saņemat valodā, kas izvēlēta sadaļā “Settings”: latviešu vai lietuviešu, ja lietotne darbojas šajā valodā, citādi – angļu. Lapas ceļvedī tā pati lejupielāde (grāmatas ikona blakus EN / LT / LV) ir pieejama ceļveža valodā.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-refresh"]',
      title: 'Reload the statements',
      body: 'Loads the statement list again from the server with the current filters. Unsaved edits in either list are discarded, so save them first.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Iš naujo įkelti išrašus',
          body: 'Iš serverio iš naujo įkelia išrašų sąrašą pagal esamus filtrus. Neišsaugoti pakeitimai abiejuose sąrašuose prarandami, todėl pirmiausia juos išsaugokite.',
        },
        lv: {
          title: 'Pārlādēt izrakstus',
          body: 'No servera vēlreiz ielādē izrakstu sarakstu ar pašreizējiem filtriem. Nesaglabātās izmaiņas abos sarakstos tiek zaudētas, tāpēc vispirms tās saglabājiet.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-results-count"]',
      title: 'See how many statements are listed',
      body: 'The number of statements currently shown in the upper list, after the search box has filtered it. Entries are not counted.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Kiek išrašų rodoma',
          body: 'Viršutiniame sąraše šiuo metu rodomų išrašų skaičius, pritaikius paieškos laukelį. Įrašai neskaičiuojami.',
        },
        lv: {
          title: 'Cik izrakstu ir sarakstā',
          body: 'Augšējā sarakstā pašlaik redzamo izrakstu skaits pēc meklēšanas lauka filtra. Ieraksti netiek skaitīti.',
        },
      },
    },

    // ----------------------------------------------------------------- Finding a statement
    {
      selector: '[data-help="bank-statements-search"]',
      title: 'Search statements and entries',
      body: 'Filters the loaded statements as you type. It matches the statement comment, account owner, company, currency, balances and dates, and in the entries the client name, client code, client account, payment reference and comment. Statements with a matching entry are ticked automatically so those entries show below; entry amounts and payment dates are not searched.',
      section: 'Finding a statement',
      i18n: {
        lt: {
          title: 'Ieškoti išrašų ir įrašų',
          body: 'Filtruoja įkeltus išrašus jums rašant. Ieškoma išrašo komentare, sąskaitos savininko, įmonės, valiutos, likučių ir datų laukuose, o įrašuose – kliento pavadinime, kliento kode, kliento sąskaitoje, mokėjimo numeryje ir komentare. Išrašai, kuriuose rastas atitinkantis įrašas, pažymimi automatiškai, todėl tie įrašai matomi apačioje; įrašų sumose ir mokėjimo datose neieškoma.',
        },
        lv: {
          title: 'Meklēt izrakstus un ierakstus',
          body: 'Filtrē ielādētos izrakstus, kamēr rakstāt. Meklē izraksta komentārā, konta īpašnieka, uzņēmuma, valūtas, atlikumu un datumu laukos, bet ierakstos – klienta nosaukumā, klienta kodā, klienta kontā, maksājuma numurā un komentārā. Izraksti ar atbilstošu ierakstu tiek atzīmēti automātiski, lai šie ieraksti būtu redzami zemāk; ierakstu summās un maksājuma datumos netiek meklēts.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-date-from"]',
      title: 'Set the start of the date range',
      body: 'Only statements whose selected date (see the date-type list) falls on or after this day are loaded. By default the range covers the last week; changing a date reloads the list from the server.',
      section: 'Finding a statement',
      i18n: {
        lt: {
          title: 'Nustatyti laikotarpio pradžią',
          body: 'Įkeliami tik tie išrašai, kurių pasirinkta data (žr. datos tipo sąrašą) yra ši diena arba vėlesnė. Pagal nutylėjimą rodoma paskutinė savaitė; pakeitus datą sąrašas iš naujo įkeliamas iš serverio.',
        },
        lv: {
          title: 'Iestatīt perioda sākumu',
          body: 'Tiek ielādēti tikai tie izraksti, kuru izvēlētais datums (sk. datuma veida sarakstu) ir šī diena vai vēlāk. Pēc noklusējuma periods aptver pēdējo nedēļu; mainot datumu, saraksts tiek pārlādēts no servera.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-date-to"]',
      title: 'Set the end of the date range',
      body: 'Only statements whose selected date falls on or before this day are loaded. Leave it at today to include the newest statements.',
      section: 'Finding a statement',
      i18n: {
        lt: {
          title: 'Nustatyti laikotarpio pabaigą',
          body: 'Įkeliami tik tie išrašai, kurių pasirinkta data yra ši diena arba ankstesnė. Palikite šiandienos datą, kad matytumėte naujausius išrašus.',
        },
        lv: {
          title: 'Iestatīt perioda beigas',
          body: 'Tiek ielādēti tikai tie izraksti, kuru izvēlētais datums ir šī diena vai agrāk. Atstājiet šodienas datumu, lai redzētu jaunākos izrakstus.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-date-field"]',
      title: 'Choose which date the range applies to',
      body: 'Pick "Statement created at", "Period from" or "Period to". The date range on the left is applied to that date, and the list reloads when you change it.',
      section: 'Finding a statement',
      i18n: {
        lt: {
          title: 'Pasirinkti, kuriai datai taikomas laikotarpis',
          body: 'Pasirinkite „Įrašas sukurtas“, „Laikotarpis nuo“ arba „Laikotarpis iki“. Kairėje nustatytas laikotarpis taikomas šiai datai, o ją pakeitus sąrašas įkeliamas iš naujo.',
        },
        lv: {
          title: 'Izvēlēties, kuram datumam piemērot periodu',
          body: 'Izvēlieties “Statement created at”, “Period from” vai “Period to”. Pa kreisi iestatītais periods tiek piemērots šim datumam, un, to mainot, saraksts tiek pārlādēts.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-companies"]',
      title: 'Choose the GrECo companies',
      body: 'Tick the companies whose bank statements you want to see; the list reloads when the menu closes. At least one company must stay selected, and the choice also applies to the other PCT pages.',
      section: 'Finding a statement',
      i18n: {
        lt: {
          title: 'Pasirinkti GrECo įmones',
          body: 'Pažymėkite įmones, kurių banko išrašus norite matyti; uždarius meniu sąrašas įkeliamas iš naujo. Bent viena įmonė turi likti pažymėta, o pasirinkimas galioja ir kituose PCT puslapiuose.',
        },
        lv: {
          title: 'Izvēlēties GrECo uzņēmumus',
          body: 'Atzīmējiet uzņēmumus, kuru bankas izrakstus vēlaties redzēt; aizverot izvēlni, saraksts tiek pārlādēts. Vismaz vienam uzņēmumam jāpaliek atzīmētam, un izvēle attiecas arī uz citām PCT lapām.',
        },
      },
    },

    // ----------------------------------------------------------------- Adding a statement
    {
      selector: '[data-help="bank-statements-add-menu"]',
      title: 'Add a bank statement',
      body: 'Opens a menu with two ways to add a statement: import the bank’s CAMT XML file, or enter a statement by hand. Importing is the normal way; manual entry is for statements you do not have as a file.',
      section: 'Adding a statement',
      i18n: {
        lt: {
          title: 'Pridėti banko išrašą',
          body: 'Atveria meniu su dviem būdais pridėti išrašą: importuoti banko CAMT XML failą arba įvesti išrašą ranka. Įprastas būdas – importas; rankinis įvedimas skirtas išrašams, kurių neturite failo pavidalu.',
        },
        lv: {
          title: 'Pievienot bankas izrakstu',
          body: 'Atver izvēlni ar diviem veidiem, kā pievienot izrakstu: importēt bankas CAMT XML failu vai ievadīt izrakstu manuāli. Parastais veids ir imports; manuālā ievade paredzēta izrakstiem, kas jums nav pieejami kā fails.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-add-import"]',
      title: 'Import a statement from an XML file',
      body: 'Opens the "Import Bank Statement" dialog, where you upload the CAMT file downloaded from the internet bank.',
      section: 'Adding a statement',
      context: 'add-menu',
      i18n: {
        lt: {
          title: 'Importuoti išrašą iš XML failo',
          body: 'Atveria langą „Import Bank Statement“, kuriame įkeliate iš internetinės bankininkystės atsisiųstą CAMT failą.',
        },
        lv: {
          title: 'Importēt izrakstu no XML faila',
          body: 'Atver logu “Import Bank Statement”, kurā augšupielādējat no internetbankas lejupielādēto CAMT failu.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-add-manual"]',
      title: 'Enter a statement by hand',
      body: 'Adds an empty statement at the top of the list, already ticked and in edit mode, with EUR as currency and today as creation date. Fill in the cells and save with the green tick in the Options column, then add its entries with "Add Bank Statement Entry".',
      section: 'Adding a statement',
      context: 'add-menu',
      i18n: {
        lt: {
          title: 'Įvesti išrašą ranka',
          body: 'Sąrašo viršuje prideda tuščią, jau pažymėtą ir redaguojamą išrašą su EUR valiuta ir šiandienos sukūrimo data. Užpildykite langelius ir išsaugokite žalia varnele stulpelyje „Nustatymai“, tada pridėkite įrašus mygtuku „Add Bank Statement Entry“.',
        },
        lv: {
          title: 'Ievadīt izrakstu manuāli',
          body: 'Saraksta augšā pievieno tukšu, jau atzīmētu un rediģējamu izrakstu ar valūtu EUR un šodienas izveides datumu. Aizpildiet šūnas un saglabājiet ar zaļo ķeksīti kolonnā “Options”, pēc tam pievienojiet ierakstus ar pogu “Add Bank Statement Entry”.',
        },
      },
    },

    // ----------------------------------------------------------------- Importing a CAMT file
    {
      selector: '[data-help="bank-statements-import-dropzone"]',
      title: 'Choose the CAMT file',
      body: 'Drag the XML file onto this area or click "Browse Files". Only CAMT.052 and CAMT.053 statements (version 001.02) are accepted; any other format makes the import fail. Use the X next to the file name or "Choose Different File" to replace it.',
      section: 'Importing a CAMT file',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti CAMT failą',
          body: 'Nutempkite XML failą į šią sritį arba spauskite „Ieškoti“. Priimami tik CAMT.052 ir CAMT.053 išrašai (versija 001.02); kitokio formato failo importas nepavyks. Failą pakeisite spausdami X šalia jo pavadinimo arba „Choose Different File“.',
        },
        lv: {
          title: 'Izvēlēties CAMT failu',
          body: 'Ievelciet XML failu šajā laukā vai noklikšķiniet uz “Browse Files”. Tiek pieņemti tikai CAMT.052 un CAMT.053 izraksti (versija 001.02); cita formāta faila imports neizdosies. Failu var nomainīt ar X blakus faila nosaukumam vai ar “Choose Different File”.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-import-submit"]',
      title: 'Import the file',
      body: 'Creates the statement with all its entries and reloads the list. The import is refused if the same statement was already imported or if it belongs to a company you may not post for; entries already imported from an overlapping file are skipped without a message. Payers are filled in automatically where they can be recognised.',
      section: 'Importing a CAMT file',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Importuoti failą',
          body: 'Sukuria išrašą su visais jo įrašais ir iš naujo įkelia sąrašą. Importas atmetamas, jei tas pats išrašas jau importuotas arba jis priklauso įmonei, kurios duomenų jums neleidžiama registruoti; įrašai, jau importuoti iš persidengiančio failo, praleidžiami be pranešimo. Mokėtojai užpildomi automatiškai, kai juos pavyksta atpažinti.',
        },
        lv: {
          title: 'Importēt failu',
          body: 'Izveido izrakstu ar visiem tā ierakstiem un pārlādē sarakstu. Imports tiek noraidīts, ja tas pats izraksts jau ir importēts vai tas pieder uzņēmumam, kura datus jums nav atļauts reģistrēt; ieraksti, kas jau importēti no pārklājoša faila, tiek izlaisti bez paziņojuma. Maksātāji tiek aizpildīti automātiski, ja tos izdodas atpazīt.',
        },
      },
    },

    // ----------------------------------------------------------------- Reading the statement list
    {
      selector: '[data-help="bank-statements-grid"]',
      title: 'Read the statement list',
      body: 'One row per bank statement, newest first: bank, status counts, paid/received totals, dates, currency, company, account owner and number, opening and closing balance (OPBD/CLBD) and comment. Click a column header to sort. Imported statements are read-only; manually entered ones can be edited in the cells.',
      section: 'Reading the statement list',
      i18n: {
        lt: {
          title: 'Skaityti išrašų sąrašą',
          body: 'Viena eilutė – vienas banko išrašas, naujausi viršuje: bankas, būsenos skaičiai, sumokėtos ir gautos sumos, datos, valiuta, įmonė, sąskaitos savininkas ir numeris, pradinis ir galutinis likutis (OPBD/CLBD) bei komentaras. Spustelėkite stulpelio antraštę, kad surikiuotumėte. Importuotų išrašų keisti negalima; ranka įvestus galima redaguoti langeliuose.',
        },
        lv: {
          title: 'Lasīt izrakstu sarakstu',
          body: 'Viena rinda – viens bankas izraksts, jaunākie augšā: banka, statusa skaitļi, samaksātās un saņemtās summas, datumi, valūta, uzņēmums, konta īpašnieks un numurs, sākuma un beigu atlikums (OPBD/CLBD) un komentārs. Noklikšķiniet uz kolonnas virsraksta, lai kārtotu. Importētie izraksti nav labojami; manuāli ievadītos var rediģēt šūnās.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-row-select"]',
      title: 'Tick a statement to see its entries',
      body: 'Ticking a row shows that statement’s entries in the lower list and reveals the extra actions in its Options column. Tick several statements to see their entries together. Save or cancel unsaved entry edits first, otherwise the tick is refused.',
      section: 'Reading the statement list',
      optional: true,
      i18n: {
        lt: {
          title: 'Pažymėti išrašą ir matyti jo įrašus',
          body: 'Pažymėjus eilutę, apatiniame sąraše rodomi to išrašo įrašai, o stulpelyje „Nustatymai“ atsiranda papildomi veiksmai. Pažymėkite kelis išrašus, kad jų įrašai būtų rodomi kartu. Pirmiausia išsaugokite arba atšaukite neišsaugotus įrašų pakeitimus, kitaip pažymėti nepavyks.',
        },
        lv: {
          title: 'Atzīmēt izrakstu, lai redzētu tā ierakstus',
          body: 'Atzīmējot rindu, apakšējā sarakstā tiek parādīti šī izraksta ieraksti, bet kolonnā “Options” parādās papildu darbības. Atzīmējiet vairākus izrakstus, lai to ierakstus redzētu kopā. Vispirms saglabājiet vai atceliet nesaglabātās ierakstu izmaiņas, citādi atzīmēt nevarēs.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-row-status"]',
      title: 'Check how far a statement is processed',
      body: 'Three counts: entries with a payer (resolved) / entries cleared / all entries. When the cleared count equals the total, every entry of the statement has been dealt with.',
      section: 'Reading the statement list',
      optional: true,
      i18n: {
        lt: {
          title: 'Patikrinti, kiek išrašas apdorotas',
          body: 'Trys skaičiai: įrašai su mokėtoju (Resolved) / užskaityti įrašai (Cleared) / visi įrašai. Kai užskaitytų skaičius lygus visų įrašų skaičiui, visi išrašo įrašai sutvarkyti.',
        },
        lv: {
          title: 'Pārbaudīt, cik tālu izraksts ir apstrādāts',
          body: 'Trīs skaitļi: ieraksti ar maksātāju (Resolved) / nokārtotie ieraksti (Cleared) / visi ieraksti. Kad nokārtoto skaits sakrīt ar kopējo, visi izraksta ieraksti ir apstrādāti.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-row-paid-received"]',
      title: 'Compare money out and money in',
      body: 'The sum of the statement’s outgoing (negative) entries and the sum of its incoming (positive) entries. Deleted entries are not counted.',
      section: 'Reading the statement list',
      optional: true,
      i18n: {
        lt: {
          title: 'Palyginti išeinančius ir gaunamus pinigus',
          body: 'Išrašo išeinančių (neigiamų) įrašų suma ir gaunamų (teigiamų) įrašų suma. Ištrinti įrašai neskaičiuojami.',
        },
        lv: {
          title: 'Salīdzināt izejošo un ienākošo naudu',
          body: 'Izraksta izejošo (negatīvo) ierakstu summa un ienākošo (pozitīvo) ierakstu summa. Dzēstie ieraksti netiek skaitīti.',
        },
      },
    },

    // ----------------------------------------------------------------- Statement actions
    {
      selector: '[data-help="bank-statements-row-suggest-clients"]',
      title: 'Suggest payers for all entries',
      body: 'Looks for a matching client for every entry of the statement that has no payer yet. Suggestions are filled into the Payer column in edit mode; nothing is saved until you confirm each entry with its green tick.',
      section: 'Statement actions',
      context: 'statement-selected',
      optional: true,
      i18n: {
        lt: {
          title: 'Pasiūlyti mokėtojus visiems įrašams',
          body: 'Ieško atitinkančio kliento kiekvienam išrašo įrašui, kuris dar neturi mokėtojo. Pasiūlymai įrašomi į stulpelį „Mokėtojas“ redagavimo režimu; niekas neišsaugoma, kol kiekvieno įrašo nepatvirtinate jo žalia varnele.',
        },
        lv: {
          title: 'Ieteikt maksātājus visiem ierakstiem',
          body: 'Meklē atbilstošu klientu katram izraksta ierakstam, kuram vēl nav maksātāja. Ieteikumi tiek ierakstīti kolonnā “Payer” rediģēšanas režīmā; nekas netiek saglabāts, kamēr katru ierakstu neapstiprināt ar tā zaļo ķeksīti.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-row-perfect-match"]',
      title: 'Clear entries that match a reminder exactly',
      body: 'Opens "Clear all perfect matches" for this statement. It clears every entry that has a payer, is not cleared yet and whose payment reference is a reminder number, or contains the number of exactly one of the client’s reminders, provided nothing has been paid on that reminder and the amounts are equal. When no reminder is named, an imported QTG invoice named in the reference clears the entry the same way: the amount must equal the invoice premium and every invoice entry must be matched to an unpaid installment; if the installments are not in GOS yet, the invoice only remembers the entry as pending.',
      section: 'Statement actions',
      context: 'statement-selected',
      optional: true,
      i18n: {
        lt: {
          title: 'Užskaityti įrašus, tiksliai atitinkančius priminimą',
          body: 'Atveria šio išrašo langą „Išvalyti visus tobulus atitikmenis“. Užskaitomas kiekvienas įrašas, kuris turi mokėtoją, dar nėra užskaitytas ir kurio mokėjimo numeris yra priminimo numeris arba jame yra lygiai vieno kliento priminimo numeris, jei pagal tą priminimą dar niekas nesumokėta ir sumos sutampa. Kai priminimo nėra, mokėjimo numeryje nurodyta importuota QTG sąskaita užskaito įrašą taip pat: suma turi sutapti su sąskaitos premija, o kiekvienas sąskaitos įrašas turi būti susietas su neapmokėta įmoka; jei įmokų GOS dar nėra, sąskaita tik įsimena įrašą kaip laukiantį.',
        },
        lv: {
          title: 'Nokārtot ierakstus, kas precīzi atbilst atgādinājumam',
          body: 'Atver šī izraksta logu “Notīrīt visas ideālās atbilstības”. Tiek nokārtots katrs ieraksts, kuram ir maksātājs, kas vēl nav nokārtots un kura maksājuma numurs ir atgādinājuma numurs vai satur tieši viena klienta atgādinājuma numuru, ja pēc šī atgādinājuma vēl nekas nav samaksāts un summas sakrīt. Ja atgādinājuma nav, maksājuma mērķī nosauktais importētais QTG rēķins nokārto ierakstu tāpat: summai jāsakrīt ar rēķina prēmiju, un katram rēķina ierakstam jābūt sasaistītam ar neapmaksātu iemaksu; ja iemaksu GOS vēl nav, rēķins ierakstu tikai atceras kā gaidošu.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-row-download"]',
      title: 'Download the original file',
      body: 'Opens the XML file the statement was imported from, through a temporary link. Only imported statements have it; manually entered ones show "Add Bank Statement Entry" in its place.',
      section: 'Statement actions',
      context: 'statement-selected',
      optional: true,
      i18n: {
        lt: {
          title: 'Atsisiųsti originalų failą',
          body: 'Laikina nuoroda atidaro XML failą, iš kurio išrašas buvo importuotas. Jį turi tik importuoti išrašai; ranka įvestuose jo vietoje rodomas mygtukas „Add Bank Statement Entry“.',
        },
        lv: {
          title: 'Lejupielādēt sākotnējo failu',
          body: 'Ar pagaidu saiti atver XML failu, no kura izraksts tika importēts. Tas ir tikai importētajiem izrakstiem; manuāli ievadītajiem tā vietā ir poga “Add Bank Statement Entry”.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-row-add-entry"]',
      title: 'Add an entry to a manual statement',
      body: 'Adds an empty entry to the lower list in edit mode, with today as payment date. Fill in at least the amount and the client account, then save with the green tick on the entry. Only manually entered statements can get new entries.',
      section: 'Statement actions',
      context: 'statement-selected',
      optional: true,
      i18n: {
        lt: {
          title: 'Pridėti įrašą į ranka įvestą išrašą',
          body: 'Apatiniame sąraše prideda tuščią redaguojamą įrašą su šiandienos mokėjimo data. Užpildykite bent sumą ir kliento sąskaitą, tada išsaugokite įrašo žalia varnele. Naujus įrašus galima pridėti tik į ranka įvestus išrašus.',
        },
        lv: {
          title: 'Pievienot ierakstu manuālam izrakstam',
          body: 'Apakšējā sarakstā pievieno tukšu rediģējamu ierakstu ar šodienas maksājuma datumu. Aizpildiet vismaz summu un klienta kontu, pēc tam saglabājiet ar ieraksta zaļo ķeksīti. Jaunus ierakstus var pievienot tikai manuāli ievadītiem izrakstiem.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-row-save"]',
      title: 'Save a manual statement',
      body: 'Appears in the Options column while a manually entered statement has unsaved changes: the green tick saves them, the red X discards them. "Period from" must not be after "Period to", and currency and company cannot be changed once the statement is saved.',
      section: 'Statement actions',
      context: 'statement-editing',
      optional: true,
      i18n: {
        lt: {
          title: 'Išsaugoti ranka įvestą išrašą',
          body: 'Rodoma stulpelyje „Nustatymai“, kol ranka įvestas išrašas turi neišsaugotų pakeitimų: žalia varnelė juos išsaugo, raudonas X atšaukia. „Laikotarpis nuo“ negali būti vėlesnis už „Laikotarpis iki“, o valiutos ir įmonės po išsaugojimo pakeisti nebegalima.',
        },
        lv: {
          title: 'Saglabāt manuālo izrakstu',
          body: 'Parādās kolonnā “Options”, kamēr manuāli ievadītam izrakstam ir nesaglabātas izmaiņas: zaļais ķeksītis tās saglabā, sarkanais X atceļ. “Period from” nedrīkst būt vēlāks par “Period to”, un pēc saglabāšanas valūtu un uzņēmumu vairs nevar mainīt.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-row-delete"]',
      title: 'Delete a statement',
      body: 'Opens the delete confirmation for this statement. Deleting removes the statement together with all its entries and reverses the clearances made from them.',
      section: 'Statement actions',
      optional: true,
      i18n: {
        lt: {
          title: 'Ištrinti išrašą',
          body: 'Atveria šio išrašo ištrynimo patvirtinimą. Ištrynus pašalinamas išrašas su visais jo įrašais, o iš jų atlikti užskaitymai atšaukiami.',
        },
        lv: {
          title: 'Dzēst izrakstu',
          body: 'Atver šī izraksta dzēšanas apstiprinājumu. Dzēšot tiek noņemts izraksts kopā ar visiem tā ierakstiem, un no tiem veiktās nokārtošanas tiek atceltas.',
        },
      },
    },

    // ----------------------------------------------------------------- Statement entries
    {
      selector: '[data-help="bank-statements-entries-grid"]',
      title: 'Work with the entries of ticked statements',
      body: 'Shows the entries of every ticked statement: payment date, amount, Payer (the client the money is resolved to), payment reference, the counterparty’s account, name and code, and comment. The Payer cell can be changed on any entry that is not cleared yet; the other cells are editable only on manually entered statements. Ticked entries mark their statements in the upper list with blue lines above and below the row, so you can see which statements the selection comes from.',
      section: 'Statement entries',
      i18n: {
        lt: {
          title: 'Dirbti su pažymėtų išrašų įrašais',
          body: 'Rodomi visų pažymėtų išrašų įrašai: mokėjimo data, suma, „Mokėtojas“ (klientas, kuriam priskirti pinigai), mokėjimo numeris, kitos šalies sąskaita, pavadinimas ir kodas bei komentaras. Langelį „Mokėtojas“ galima keisti bet kuriame dar neužskaitytame įraše; kitus langelius galima redaguoti tik ranka įvestuose išrašuose. Pažymėti įrašai viršutiniame sąraše pažymi savo išrašus mėlynomis linijomis virš ir po eilute, todėl matyti, iš kurių išrašų yra pasirinkti įrašai.',
        },
        lv: {
          title: 'Strādāt ar atzīmēto izrakstu ierakstiem',
          body: 'Parāda visu atzīmēto izrakstu ierakstus: maksājuma datums, summa, “Payer” (klients, kuram nauda piesaistīta), maksājuma numurs, otras puses konts, nosaukums un kods, kā arī komentārs. Šūnu “Payer” var mainīt jebkuram vēl nenokārtotam ierakstam; pārējās šūnas var rediģēt tikai manuāli ievadītiem izrakstiem. Atzīmētie ieraksti augšējā sarakstā iezīmē savus izrakstus ar zilām līnijām virs un zem rindas, lai būtu redzams, no kuriem izrakstiem nāk atlase.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-entry-filters"]',
      title: 'Show negative or hidden entries',
      body: 'By default the lower list shows only incoming payments that are not hidden. Tick "Include negative amounts" to see outgoing payments as well, and "Include hidden entries" to bring back entries you hid. Both choices are remembered in this browser.',
      section: 'Statement entries',
      i18n: {
        lt: {
          title: 'Rodyti neigiamus arba paslėptus įrašus',
          body: 'Pagal nutylėjimą apatiniame sąraše rodomi tik nepaslėpti gaunami mokėjimai. Pažymėkite „Įtraukti neigiamas sumas“, kad matytumėte ir išeinančius mokėjimus, ir „Įtraukti paslėptus įrašus“, kad grąžintumėte paslėptus įrašus. Abu pasirinkimai įsimenami šioje naršyklėje.',
        },
        lv: {
          title: 'Rādīt negatīvos vai paslēptos ierakstus',
          body: 'Pēc noklusējuma apakšējā sarakstā redzami tikai nepaslēpti ienākošie maksājumi. Atzīmējiet “Iekļaut negatīvās summas”, lai redzētu arī izejošos maksājumus, un “Iekļaut paslēptos ierakstus”, lai atgrieztu paslēptos ierakstus. Abas izvēles tiek atcerētas šajā pārlūkā.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-entry-status"]',
      title: 'Read an entry’s status',
      body: 'The icon shows where the entry stands: question mark – no payer yet; outlined tick – payer set (resolved); filled tick – cleared; pencil – unsaved changes. Hover it to see the name.',
      section: 'Statement entries',
      context: 'entries-shown',
      optional: true,
      i18n: {
        lt: {
          title: 'Suprasti įrašo būseną',
          body: 'Piktograma rodo, kurioje stadijoje yra įrašas: klaustukas – mokėtojo dar nėra; kontūrinė varnelė – mokėtojas priskirtas (Resolved); užpildyta varnelė – užskaitytas; pieštukas – yra neišsaugotų pakeitimų. Užveskite pelę, kad pamatytumėte pavadinimą.',
        },
        lv: {
          title: 'Saprast ieraksta statusu',
          body: 'Ikona rāda, kādā stadijā ir ieraksts: jautājuma zīme – maksātāja vēl nav; kontūras ķeksītis – maksātājs piesaistīts (Resolved); aizpildīts ķeksītis – nokārtots (Cleared); zīmulis – ir nesaglabātas izmaiņas. Novietojiet peli virs ikonas, lai redzētu nosaukumu.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-entry-hide"]',
      title: 'Hide or show an entry',
      body: 'The eye button hides an entry you do not need to work on; it disappears from the list unless "Include hidden entries" is ticked. Hiding is saved at once and can be undone with the same button.',
      section: 'Statement entries',
      context: 'entries-shown',
      optional: true,
      i18n: {
        lt: {
          title: 'Paslėpti arba rodyti įrašą',
          body: 'Akies mygtukas paslepia įrašą, su kuriuo dirbti nereikia; jis dingsta iš sąrašo, nebent pažymėta „Įtraukti paslėptus įrašus“. Paslėpimas išsaugomas iš karto ir atšaukiamas tuo pačiu mygtuku.',
        },
        lv: {
          title: 'Paslēpt vai parādīt ierakstu',
          body: 'Acs poga paslēpj ierakstu, ar kuru nav jāstrādā; tas pazūd no saraksta, ja nav atzīmēts “Iekļaut paslēptos ierakstus”. Paslēpšana tiek saglabāta uzreiz, un to var atcelt ar to pašu pogu.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-entry-suggest-client"]',
      title: 'Suggest the payer for one entry',
      body: 'Shown on entries without a payer. Looks up a matching client and fills it into the Payer cell in edit mode; save it with the green tick. If nothing matches, a message says no suggestion is available and you can pick the client in the Payer cell yourself.',
      section: 'Statement entries',
      context: 'entries-shown',
      optional: true,
      i18n: {
        lt: {
          title: 'Pasiūlyti mokėtoją vienam įrašui',
          body: 'Rodoma įrašuose be mokėtojo. Suranda atitinkantį klientą ir įrašo jį į langelį „Mokėtojas“ redagavimo režimu; išsaugokite žalia varnele. Jei atitikmens nėra, pranešama, kad pasiūlymo nėra, ir klientą langelyje „Mokėtojas“ galite pasirinkti patys.',
        },
        lv: {
          title: 'Ieteikt maksātāju vienam ierakstam',
          body: 'Redzama ierakstiem bez maksātāja. Atrod atbilstošu klientu un ieraksta to šūnā “Payer” rediģēšanas režīmā; saglabājiet ar zaļo ķeksīti. Ja atbilstības nav, tiek parādīts paziņojums, ka ieteikuma nav, un klientu šūnā “Payer” varat izvēlēties paši.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-entry-save"]',
      title: 'Save the entry',
      body: 'The green tick saves the entry’s changes — for a new payer this records the resolvement on the server; the red X discards them. Rows with unsaved changes show a pencil in the status column.',
      section: 'Statement entries',
      context: 'entry-editing',
      optional: true,
      i18n: {
        lt: {
          title: 'Išsaugoti įrašą',
          body: 'Žalia varnelė išsaugo įrašo pakeitimus – naujas mokėtojas taip priskiriamas serveryje; raudonas X pakeitimus atšaukia. Eilutėse su neišsaugotais pakeitimais būsenos stulpelyje rodomas pieštukas.',
        },
        lv: {
          title: 'Saglabāt ierakstu',
          body: 'Zaļais ķeksītis saglabā ieraksta izmaiņas – jauns maksātājs tādējādi tiek piesaistīts serverī; sarkanais X izmaiņas atceļ. Rindām ar nesaglabātām izmaiņām statusa kolonnā redzams zīmulis.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-entry-clear"]',
      title: 'Clear the entry',
      body: 'Shown once an entry has a payer and is not cleared. For incoming money it opens the clearance dialog, where the amount is matched to the client’s open premium; for a negative or zero amount it asks to book the amount to the client’s balance instead.',
      section: 'Statement entries',
      context: 'entries-shown',
      optional: true,
      i18n: {
        lt: {
          title: 'Užskaityti įrašą',
          body: 'Rodoma, kai įrašas turi mokėtoją ir dar nėra užskaitytas. Gautiems pinigams atveria užskaitymo langą, kuriame suma priskiriama neapmokėtoms kliento įmokoms; neigiamos arba nulinės sumos atveju siūloma visą sumą įrašyti į kliento balansą.',
        },
        lv: {
          title: 'Nokārtot ierakstu',
          body: 'Redzama, kad ierakstam ir maksātājs un tas vēl nav nokārtots. Ienākošai naudai atver nokārtošanas logu, kurā summa tiek piesaistīta klienta neapmaksātajai prēmijai; negatīvas vai nulles summas gadījumā tiek piedāvāts visu summu iegrāmatot klienta saldo.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-entry-info"]',
      title: 'See how an entry was cleared',
      body: 'Shown on cleared entries, except outgoing payments to insurers. Opens the list of payments the clearance created and the part that went to the client’s balance.',
      section: 'Statement entries',
      context: 'entries-shown',
      optional: true,
      i18n: {
        lt: {
          title: 'Pažiūrėti, kaip įrašas užskaitytas',
          body: 'Rodoma užskaitytuose įrašuose, išskyrus mokėjimus draudikams. Atveria užskaitymo metu sukurtų mokėjimų sąrašą ir dalį, kuri atiteko kliento balansui.',
        },
        lv: {
          title: 'Apskatīt, kā ieraksts nokārtots',
          body: 'Redzama nokārtotiem ierakstiem, izņemot maksājumus apdrošinātājiem. Atver nokārtošanas laikā izveidoto maksājumu sarakstu un daļu, kas nonāca klienta saldo.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-entry-delete"]',
      title: 'Delete an entry',
      body: 'Available only on manually entered statements; entries of imported statements cannot be deleted one by one. Deleting a cleared entry also reverses its clearance.',
      section: 'Statement entries',
      context: 'entries-shown',
      optional: true,
      i18n: {
        lt: {
          title: 'Ištrinti įrašą',
          body: 'Galima tik ranka įvestuose išrašuose; importuotų išrašų įrašų po vieną ištrinti negalima. Ištrynus užskaitytą įrašą, atšaukiamas ir jo užskaitymas.',
        },
        lv: {
          title: 'Dzēst ierakstu',
          body: 'Pieejams tikai manuāli ievadītiem izrakstiem; importēto izrakstu ierakstus nevar dzēst pa vienam. Dzēšot nokārtotu ierakstu, tiek atcelta arī tā nokārtošana.',
        },
      },
    },

    // ----------------------------------------------------------------- Clearing perfect matches
    {
      selector: '[data-help="bank-statements-perfect-match-confirm"]',
      title: 'Run the perfect-match clearance',
      body: 'Click "Confirm" to clear all matching entries of the statement in one go. The dialog then shows how many entries were cleared and offers "Export Excel" with the list; after you close it, those entries are highlighted in the lower list.',
      section: 'Clearing perfect matches',
      context: 'perfect-match-dialog',
      i18n: {
        lt: {
          title: 'Paleisti tobulų atitikmenų užskaitymą',
          body: 'Spauskite „Patvirtinti“, kad visi atitinkantys išrašo įrašai būtų užskaityti vienu kartu. Tada lange parodoma, kiek įrašų užskaityta, ir siūloma „Export Excel“ su jų sąrašu; uždarius langą tie įrašai apatiniame sąraše paryškinami.',
        },
        lv: {
          title: 'Palaist ideālo atbilstību nokārtošanu',
          body: 'Noklikšķiniet uz “Apstiprināt”, lai visus atbilstošos izraksta ierakstus nokārtotu vienā reizē. Pēc tam logā redzams, cik ierakstu nokārtots, un piedāvāts “Export Excel” ar to sarakstu; pēc loga aizvēršanas šie ieraksti apakšējā sarakstā ir izcelti.',
        },
      },
    },

    // ----------------------------------------------------------------- Deleting a statement
    {
      selector: '[data-help="bank-statements-delete-comment"]',
      title: 'Explain why the statement is deleted',
      body: 'The comment is required for imported statements — "Delete" stays disabled until you write one — and optional for manually entered ones.',
      section: 'Deleting a statement',
      context: 'delete-dialog',
      i18n: {
        lt: {
          title: 'Nurodyti išrašo ištrynimo priežastį',
          body: 'Importuotiems išrašams komentaras privalomas – „Ištrinti“ lieka neaktyvus, kol jo neparašote, – o ranka įvestiems jis neprivalomas.',
        },
        lv: {
          title: 'Norādīt izraksta dzēšanas iemeslu',
          body: 'Importētiem izrakstiem komentārs ir obligāts – “Delete” paliek neaktīvs, kamēr to neierakstāt, – bet manuāli ievadītiem tas nav obligāts.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-delete-submit"]',
      title: 'Confirm the deletion',
      body: 'Deletes the statement and all its entries, reverses any clearance made from them and reloads the list. There is no undo in the app; import the file again if it was deleted by mistake.',
      section: 'Deleting a statement',
      context: 'delete-dialog',
      i18n: {
        lt: {
          title: 'Patvirtinti ištrynimą',
          body: 'Ištrina išrašą ir visus jo įrašus, atšaukia iš jų atliktus užskaitymus ir iš naujo įkelia sąrašą. Programoje atšaukti negalima; jei ištrynėte per klaidą, failą importuokite dar kartą.',
        },
        lv: {
          title: 'Apstiprināt dzēšanu',
          body: 'Dzēš izrakstu un visus tā ierakstus, atceļ no tiem veiktās nokārtošanas un pārlādē sarakstu. Lietotnē to atsaukt nevar; ja izdzēsāt kļūdas pēc, importējiet failu vēlreiz.',
        },
      },
    },

    // ----------------------------------------------------------------- Clearing an entry
    {
      selector: '[data-help="bank-statements-clear-dates"]',
      title: 'Limit the premium by booking date',
      body: 'Only the client’s open payment-plan entries with a booking date in this range are listed; by default it runs from three months ago to one month ahead. Widen it if older premium is being paid, then click "Search".',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Apriboti įmokas pagal registravimo datą',
          body: 'Rodomi tik tie neapmokėti kliento mokėjimų plano įrašai, kurių registravimo data patenka į šį laikotarpį; pagal nutylėjimą jis apima laiką nuo prieš tris mėnesius iki mėnesio į priekį. Jei apmokamos senesnės įmokos, išplėskite laikotarpį ir spauskite „Ieškoti“.',
        },
        lv: {
          title: 'Ierobežot prēmiju pēc grāmatošanas datuma',
          body: 'Tiek rādīti tikai tie klienta neapmaksātie maksājumu grafika ieraksti, kuru grāmatošanas datums ir šajā periodā; pēc noklusējuma tas ir no trim mēnešiem atpakaļ līdz vienam mēnesim uz priekšu. Ja tiek apmaksāta vecāka prēmija, paplašiniet periodu un noklikšķiniet uz “Search”.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-client"]',
      title: 'Check the client',
      body: 'Prefilled with the entry’s payer; you can add more clients to look at their premium too. At least one client is required, otherwise "Search" stays disabled.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Patikrinti klientą',
          body: 'Iš anksto užpildyta įrašo mokėtoju; galite pridėti daugiau klientų ir matyti ir jų įmokas. Būtinas bent vienas klientas, kitaip „Ieškoti“ lieka neaktyvus.',
        },
        lv: {
          title: 'Pārbaudīt klientu',
          body: 'Iepriekš aizpildīts ar ieraksta maksātāju; varat pievienot vēl klientus, lai redzētu arī viņu prēmiju. Nepieciešams vismaz viens klients, citādi “Search” paliek neaktīvs.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-insurer"]',
      title: 'Filter by insurer',
      body: 'Pick an insurer country and then one or more insurers to list only the premium of their policies. Click "Search" after changing the filter.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal draudiką',
          body: 'Pasirinkite draudiko šalį, tada vieną ar kelis draudikus, kad būtų rodomos tik jų polisų įmokos. Pakeitę filtrą spauskite „Ieškoti“.',
        },
        lv: {
          title: 'Filtrēt pēc apdrošinātāja',
          body: 'Izvēlieties apdrošinātāja valsti un pēc tam vienu vai vairākus apdrošinātājus, lai redzētu tikai to polišu prēmiju. Pēc filtra maiņas noklikšķiniet uz “Search”.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-reference"]',
      title: 'Filter by payment reference',
      body: 'Prefilled with the entry’s payment reference number, which lists only premium from reminders whose number appears in it, plus the installments of an imported QTG invoice named in it. Remove it to see all of the client’s open premium, or enter other reminder numbers.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal mokėjimo numerį',
          body: 'Iš anksto užpildyta įrašo mokėjimo numeriu – tada rodomos tik tų priminimų įmokos, kurių numeris jame yra, ir jame nurodytos importuotos QTG sąskaitos įmokos. Pašalinkite jį, kad matytumėte visas neapmokėtas kliento įmokas, arba įveskite kitus priminimų numerius.',
        },
        lv: {
          title: 'Filtrēt pēc maksājuma numura',
          body: 'Iepriekš aizpildīts ar ieraksta maksājuma numuru – tad tiek rādīta tikai to atgādinājumu prēmija, kuru numurs tajā ir ietverts, kā arī tajā nosauktā importētā QTG rēķina iemaksas. Noņemiet to, lai redzētu visu klienta neapmaksāto prēmiju, vai ievadiet citus atgādinājumu numurus.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-policy"]',
      title: 'Filter by policy',
      body: 'Enter one or more policy numbers to list only their premium; the field below it does the same for policy sub-numbers. Payer and service-segment filters are above.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal polisą',
          body: 'Įveskite vieną ar kelis polisų numerius, kad būtų rodomos tik jų įmokos; laukas po juo tą patį daro su polisų subnumeriais. Mokėtojo ir paslaugų segmento filtrai yra aukščiau.',
        },
        lv: {
          title: 'Filtrēt pēc polises',
          body: 'Ievadiet vienu vai vairākus polišu numurus, lai redzētu tikai to prēmiju; lauks zem tā to pašu dara ar polišu apakšnumuriem. Maksātāja un pakalpojumu segmenta filtri atrodas augstāk.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-broker-collection"]',
      title: 'Choose which premium a discount payment covers',
      body: 'Shown only for payments from GrECo’s own collection account, which are cleared against discounts (the dialog title says "(discounts)"). Yes lists broker premium collection policies only, No the others, "Select all" both; click "Search" after changing it.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Pasirinkti, kurias įmokas dengia nuolaidų mokėjimas',
          body: 'Rodoma tik mokėjimams iš GrECo surinkimo sąskaitos, kurie užskaitomi nuolaidoms (lango pavadinime – „(discounts)“). „Taip“ rodo tik polisus, kurių įmokas surenka brokeris, „Ne“ – kitus, „Pasirinkti viską“ – abu; pakeitę spauskite „Ieškoti“.',
        },
        lv: {
          title: 'Izvēlēties, kādu prēmiju sedz atlaižu maksājums',
          body: 'Redzams tikai maksājumiem no GrECo iekasēšanas konta, kurus nokārto pret atlaidēm (loga nosaukumā – “(discounts)”). “Yes” rāda tikai polises, kuru prēmiju iekasē brokeris, “No” – pārējās, “Select all” – abas; pēc maiņas noklikšķiniet uz “Search”.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-search"]',
      title: 'Load the open premium',
      body: 'Lists the client’s unsettled payment-plan entries in the entry’s currency that match the filters, and pre-fills "To be cleared" by spreading the amount over them in list order. The list loads by itself when the dialog opens, except for discount payments; click "Search" again after changing a filter.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Įkelti neapmokėtas įmokas',
          body: 'Parodo filtrus atitinkančius neapmokėtus kliento mokėjimų plano įrašus įrašo valiuta ir iš anksto užpildo stulpelį „To be cleared“, paskirstydamas sumą sąrašo tvarka. Atidarius langą sąrašas įkeliamas savaime, išskyrus nuolaidų mokėjimus; pakeitę filtrą dar kartą spauskite „Ieškoti“.',
        },
        lv: {
          title: 'Ielādēt neapmaksāto prēmiju',
          body: 'Parāda filtriem atbilstošos klienta neapmaksātos maksājumu grafika ierakstus ieraksta valūtā un iepriekš aizpilda kolonnu “To be cleared”, sadalot summu saraksta secībā. Atverot logu, saraksts ielādējas pats, izņemot atlaižu maksājumus; pēc filtra maiņas vēlreiz noklikšķiniet uz “Search”.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-grid"]',
      title: 'Review the premium to be paid',
      body: 'Each row is an open payment-plan entry: policy, reminder number, QTG invoice, payer, client, insurer, insurance line, booking date, premium period, premium, discount, what is already paid and the remaining debt. Installments matched to an imported QTG invoice named in the payment reference are listed first, with the invoice number in the "QTG invoice" column, and get the money first. A row on a light-red background belongs to a cancelled policy — check it before clearing money against it. The "To be cleared" column on the right holds the amount this payment settles on that row; type a value to change it.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Peržiūrėti apmokamas įmokas',
          body: 'Kiekviena eilutė – neapmokėtas mokėjimų plano įrašas: polisas, priminimo numeris, QTG sąskaita, mokėtojas, klientas, draudikas, draudimo rūšis, registravimo data, įmokos laikotarpis, įmoka, nuolaida, jau sumokėta dalis ir likusi skola. Įmokos, susietos su mokėjimo numeryje nurodyta importuota QTG sąskaita, rodomos pirmos su sąskaitos numeriu stulpelyje „QTG sąskaita“ ir pinigus gauna pirmos. Šviesiai raudoname fone rodoma eilutė priklauso nutrauktam polisui – patikrinkite ją prieš užskaitydami pinigus. Dešinėje esančiame stulpelyje „To be cleared“ nurodoma, kiek šis mokėjimas padengia toje eilutėje; įrašykite reikšmę, kad ją pakeistumėte.',
        },
        lv: {
          title: 'Pārskatīt apmaksājamo prēmiju',
          body: 'Katra rinda ir neapmaksāts maksājumu grafika ieraksts: polise, atgādinājuma numurs, QTG rēķins, maksātājs, klients, apdrošinātājs, apdrošināšanas veids, grāmatošanas datums, prēmijas periods, prēmija, atlaide, jau samaksātā daļa un atlikušais parāds. Iemaksas, kas sasaistītas ar maksājuma mērķī nosaukto importēto QTG rēķinu, tiek rādītas pirmās ar rēķina numuru kolonnā “QTG rēķins” un naudu saņem pirmās. Rinda gaiši sarkanā fonā pieder anulētai polisei – pārbaudiet to, pirms nokārtojat naudu pret to. Labajā kolonnā “To be cleared” ir summa, ko šis maksājums nosedz šajā rindā; ierakstiet vērtību, lai to mainītu.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-fill-all"]',
      title: 'Fill or empty all amounts at once',
      body: 'In the "To be cleared" header the lightning button ("Select all") fills the rows from the top, each up to its debt, until the payment is used up; the X next to it ("Clear all") sets every row to zero.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Užpildyti arba išvalyti visas sumas iš karto',
          body: 'Stulpelio „To be cleared“ antraštėje žaibo mygtukas („Pasirinkti viską“) pildo eilutes iš viršaus, kiekvieną iki jos skolos, kol išnaudojama mokėjimo suma; šalia esantis X („Clear all“) visas eilutes nustato į nulį.',
        },
        lv: {
          title: 'Aizpildīt vai notīrīt visas summas uzreiz',
          body: 'Kolonnas “To be cleared” virsrakstā zibens poga (“Select all”) aizpilda rindas no augšas, katru līdz tās parādam, kamēr maksājuma summa izlietota; blakus esošais X (“Clear all”) visas rindas iestata uz nulli.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-row-fill"]',
      title: 'Fill or empty one row',
      body: 'The lightning button puts the still unallocated part of the payment on this row, up to the row’s debt; the X next to it sets the row to zero.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Užpildyti arba išvalyti vieną eilutę',
          body: 'Žaibo mygtukas šiai eilutei priskiria dar nepaskirstytą mokėjimo dalį, bet ne daugiau nei eilutės skola; šalia esantis X eilutę nustato į nulį.',
        },
        lv: {
          title: 'Aizpildīt vai notīrīt vienu rindu',
          body: 'Zibens poga šai rindai piešķir vēl nesadalīto maksājuma daļu, bet ne vairāk par rindas parādu; blakus esošais X iestata rindu uz nulli.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-total"]',
      title: 'Check what is left to allocate',
      body: 'Shows the total of the "To be cleared" column against the entry amount, and how many rows are used. It turns red when the total exceeds the payment, and "Clear" stays disabled until you fix it. Whatever is not allocated goes to the client’s balance when you clear.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Patikrinti, kiek liko paskirstyti',
          body: 'Rodo stulpelio „To be cleared“ sumą, palyginti su įrašo suma, ir kiek eilučių panaudota. Suma tampa raudona, kai viršija mokėjimą, ir „Išvalyti“ lieka neaktyvus, kol to neištaisote. Tai, kas nepaskirstyta, užskaitant atitenka kliento balansui.',
        },
        lv: {
          title: 'Pārbaudīt, cik vēl jāsadala',
          body: 'Rāda kolonnas “To be cleared” kopsummu salīdzinājumā ar ieraksta summu un izmantoto rindu skaitu. Tā kļūst sarkana, ja kopsumma pārsniedz maksājumu, un “Clear” paliek neaktīvs, kamēr to neizlabojat. Viss nesadalītais, nokārtojot ierakstu, nonāk klienta saldo.',
        },
      },
    },
    {
      selector: '[data-help="bank-statements-clear-submit"]',
      title: 'Clear the entry',
      body: 'Creates a payment for every row with an amount in "To be cleared", books any remainder to the client’s balance, marks the entry cleared and closes the dialog. Disabled until the premium list has been loaded, and while the total exceeds the payment.',
      section: 'Clearing an entry',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Užskaityti įrašą',
          body: 'Kiekvienai eilutei su suma stulpelyje „To be cleared“ sukuria mokėjimą, likutį įrašo į kliento balansą, pažymi įrašą kaip užskaitytą ir uždaro langą. Mygtukas („Išvalyti“) neaktyvus, kol neįkeltas įmokų sąrašas ir kol suma viršija mokėjimą.',
        },
        lv: {
          title: 'Nokārtot ierakstu',
          body: 'Katrai rindai ar summu kolonnā “To be cleared” izveido maksājumu, atlikumu iegrāmato klienta saldo, atzīmē ierakstu kā nokārtotu un aizver logu. Poga (“Clear”) nav aktīva, kamēr prēmiju saraksts nav ielādēts un kamēr kopsumma pārsniedz maksājumu.',
        },
      },
    },

    // ----------------------------------------------------------------- Clearing to client balance
    {
      selector: '[data-help="bank-statements-clear-saldo-submit"]',
      title: 'Book a negative amount to the client’s balance',
      body: 'An entry with a negative or zero amount has no premium to match, so "Clear" books the whole amount to the payer’s client balance and marks the entry cleared. Check the amount and the client named in the question before you confirm.',
      section: 'Clearing to client balance',
      context: 'saldo-dialog',
      i18n: {
        lt: {
          title: 'Įrašyti neigiamą sumą į kliento balansą',
          body: 'Įrašui su neigiama arba nuline suma nėra įmokų, kurias būtų galima užskaityti, todėl „Clear“ visą sumą įrašo į mokėtojo kliento balansą ir pažymi įrašą kaip užskaitytą. Prieš patvirtindami patikrinkite klausime nurodytą sumą ir klientą.',
        },
        lv: {
          title: 'Iegrāmatot negatīvu summu klienta saldo',
          body: 'Ierakstam ar negatīvu vai nulles summu nav prēmijas, ar ko to sasaistīt, tāpēc “Clear” visu summu iegrāmato maksātāja klienta saldo un atzīmē ierakstu kā nokārtotu. Pirms apstiprināšanas pārbaudiet jautājumā norādīto summu un klientu.',
        },
      },
    },

    // ----------------------------------------------------------------- Deleting an entry
    {
      selector: '[data-help="bank-statements-entry-delete-submit"]',
      title: 'Confirm deleting the entry',
      body: 'Deletes the entry; the comment above is optional. If the entry was cleared, its clearance is reversed as well.',
      section: 'Deleting an entry',
      context: 'delete-entry-dialog',
      i18n: {
        lt: {
          title: 'Patvirtinti įrašo ištrynimą',
          body: 'Ištrina įrašą; komentaras viršuje neprivalomas. Jei įrašas buvo užskaitytas, atšaukiamas ir jo užskaitymas.',
        },
        lv: {
          title: 'Apstiprināt ieraksta dzēšanu',
          body: 'Dzēš ierakstu; komentārs augšā nav obligāts. Ja ieraksts bija nokārtots, tiek atcelta arī tā nokārtošana.',
        },
      },
    },

    // ----------------------------------------------------------------- Clearance information
    {
      selector: '[data-help="bank-statements-info-summary"]',
      title: 'See what was cleared and what went to the client balance',
      body: 'The list above shows each payment created when the entry was cleared: policy, client, insurer, reminder, dates and premium amount. "Cleared" compares their total with the entry amount, and "Moved To client balance" shows the remainder, if any. "Export Excel" saves the list.',
      section: 'Clearance information',
      context: 'info-dialog',
      i18n: {
        lt: {
          title: 'Pažiūrėti, kas užskaityta ir kas atiteko kliento balansui',
          body: 'Sąraše viršuje rodomas kiekvienas užskaitant sukurtas mokėjimas: polisas, klientas, draudikas, priminimas, datos ir įmokos suma. „Cleared“ palygina jų sumą su įrašo suma, o „Moved To client balance“ rodo likutį, jei jis yra. „Export Excel“ išsaugo sąrašą.',
        },
        lv: {
          title: 'Apskatīt, kas nokārtots un kas nonāca klienta saldo',
          body: 'Sarakstā augšā redzams katrs nokārtošanas laikā izveidotais maksājums: polise, klients, apdrošinātājs, atgādinājums, datumi un prēmijas summa. “Cleared” salīdzina to kopsummu ar ieraksta summu, bet “Moved To client balance” rāda atlikumu, ja tāds ir. “Export Excel” saglabā sarakstu.',
        },
      },
    },
  ],
  i18n: {
    lt: {
      title: 'Banko išrašai',
      intro:
        'Čia gauti pinigai patenka į PCT: importuokite banko CAMT failą, įsitikinkite, kad kiekvienas įrašas turi mokėtoją (klientą, kuriam priklauso pinigai), tada užskaitykite jį neapmokėtoms to kliento įmokoms. Užskaityti pinigai tampa mokėjimais, kurie toliau tvarkomi puslapyje „Mokėjimai“ iki deklaravimo draudikams; įmokoms nepriskirta suma atitenka kliento balansui.',
      sections: {
        'Getting around': 'Orientavimasis',
        'Finding a statement': 'Išrašo paieška',
        'Adding a statement': 'Išrašo pridėjimas',
        'Importing a CAMT file': 'CAMT failo importas',
        'Reading the statement list': 'Išrašų sąrašas',
        'Statement actions': 'Veiksmai su išrašu',
        'Statement entries': 'Išrašo įrašai',
        'Clearing perfect matches': 'Tobulų atitikmenų užskaitymas',
        'Deleting a statement': 'Išrašo ištrynimas',
        'Clearing an entry': 'Įrašo užskaitymas',
        'Clearing to client balance': 'Užskaitymas į kliento balansą',
        'Deleting an entry': 'Įrašo ištrynimas',
        'Clearance information': 'Užskaitymo informacija',
      },
      contexts: {
        'add-menu': 'Antroje įrankių juostos eilutėje spauskite „Pridėkite banko išrašą“.',
        'import-dialog': 'Spauskite „Pridėkite banko išrašą“ ir pasirinkite „Import from XML file“.',
        'statement-selected':
          'Pažymėkite langelį išrašo eilutės pradžioje; papildomi mygtukai atsiras tos eilutės stulpelyje „Nustatymai“.',
        'statement-editing':
          'Meniu „Pridėkite banko išrašą“ pasirinkite „Enter manually“ arba pakeiskite ranka įvesto išrašo langelį.',
        'entries-shown': 'Viršutiniame sąraše pažymėkite išrašą; jo įrašai atsiras apatiniame sąraše.',
        'entry-editing':
          'Pakeiskite įrašo langelį „Mokėtojas“ arba išraše ar įraše naudokite „Suggest client resolvement“.',
        'perfect-match-dialog':
          'Pažymėkite išrašą ir jo stulpelyje „Nustatymai“ spauskite „Išvalyti visus tobulus atitikmenis“ (apskritų rodyklių piktograma).',
        'delete-dialog': 'Išrašo stulpelyje „Nustatymai“ spauskite „Ištrinti banko įšrašą“ (šiukšliadėžės piktograma).',
        'clear-dialog':
          'Pažymėkite išrašą, tada įraše, kuris turi mokėtoją ir teigiamą sumą, spauskite „Clear bank statement entry“.',
        'saldo-dialog':
          'Pažymėkite „Įtraukti neigiamas sumas“, tada įraše, kuris turi mokėtoją ir neigiamą sumą, spauskite „Clear bank statement entry“.',
        'delete-entry-dialog':
          'Ranka įvesto išrašo įraše spauskite „Ištrinti banko išrašo įrašą“ (šiukšliadėžės piktograma).',
        'info-dialog': 'Užskaitytame įraše spauskite „Užskaitymo informacija“ (i piktograma).',
      },
      tips: [
        'Importuoti išrašai skirti tik skaityti: jų langelių redaguoti ir įrašų po vieną trinti negalima – galima tik nurodyti mokėtoją. Jei failas buvo klaidingas, ištrinkite visą išrašą (su komentaru) ir importuokite jį iš naujo.',
        'Importuojant PCT atpažįsta mokėjimus iš GrECo surinkimo sąskaitos, draudikų sąskaitų, GrECo komisinių sąskaitų ir banko mokesčius. Išeinantys mokėjimai draudikams, GrECo komisiniai ir banko mokesčiai iš karto pažymimi kaip užskaityti.',
        'Mokėtojo pasiūlymai paeiliui tikrina kliento numerį prieš pirmąjį „-“ mokėjimo numeryje, mokėtojo įmonės ar asmens kodą ir mokėtojo pavadinimą. Jei mokėjimo numeris ir kodas rodo į skirtingus klientus, niekas nesiūloma.',
        'Apatiniame sąraše paryškinti įrašai buvo užskaityti automatiškai per „Išvalyti visus tobulus atitikmenis“.',
        'GrECo Latvia: įrašą, kurio mokėjimo numeryje nurodyta importuota QTG sąskaita, „Išvalyti visus tobulus atitikmenis“ užskaito, kai suma sutampa su sąskaitos premija ir kiekvienas sąskaitos įrašas turi įmoką; kitaip stulpelis „Laukianti QTG sąskaita“ rodo sąskaitą, kol polisas atsiranda GOS ir įrašas užskaitomas ranka, o užskaitymas sąskaitos įmokas rodo pirmas. QTG sąskaitų stulpeliai rodomi tik naudotojams, turintiems prieigą prie Latvijos GrECo įmonės.',
        'Įmonių pasirinkimas viršuje dešinėje bendras su kitais PCT puslapiais ir lemia, kurie išrašai įkeliami.',
      ],
    },
    lv: {
      title: 'Bankas konta izraksti',
      intro:
        'Šeit ienākošā nauda nonāk PCT: importējiet bankas CAMT failu, pārliecinieties, ka katram ierakstam ir maksātājs (klients, kuram nauda pieder), un tad nokārtojiet to pret šī klienta neapmaksāto prēmiju. Nokārtotā nauda kļūst par maksājumiem, kas tālāk tiek apstrādāti lapā “Payments” līdz deklarēšanai apdrošinātājiem; prēmijai nepiesaistītā summa nonāk klienta saldo.',
      sections: {
        'Getting around': 'Orientēšanās',
        'Finding a statement': 'Izraksta meklēšana',
        'Adding a statement': 'Izraksta pievienošana',
        'Importing a CAMT file': 'CAMT faila imports',
        'Reading the statement list': 'Izrakstu saraksts',
        'Statement actions': 'Darbības ar izrakstu',
        'Statement entries': 'Izraksta ieraksti',
        'Clearing perfect matches': 'Ideālo atbilstību nokārtošana',
        'Deleting a statement': 'Izraksta dzēšana',
        'Clearing an entry': 'Ieraksta nokārtošana',
        'Clearing to client balance': 'Nokārtošana uz klienta saldo',
        'Deleting an entry': 'Ieraksta dzēšana',
        'Clearance information': 'Nokārtošanas informācija',
      },
      contexts: {
        'add-menu': 'Rīkjoslas otrajā rindā noklikšķiniet uz “Add bank statement”.',
        'import-dialog': 'Noklikšķiniet uz “Add bank statement” un izvēlieties “Import from XML file”.',
        'statement-selected':
          'Atzīmējiet izvēles rūtiņu izraksta rindas sākumā; papildu pogas parādīsies šīs rindas kolonnā “Options”.',
        'statement-editing':
          'Izvēlnē “Add bank statement” izvēlieties “Enter manually” vai mainiet manuāli ievadīta izraksta šūnu.',
        'entries-shown': 'Augšējā sarakstā atzīmējiet izrakstu; tā ieraksti parādīsies apakšējā sarakstā.',
        'entry-editing':
          'Mainiet ieraksta šūnu “Payer” vai izrakstā vai ierakstā izmantojiet “Suggest client resolvement”.',
        'perfect-match-dialog':
          'Atzīmējiet izrakstu un tā kolonnā “Options” noklikšķiniet uz “Notīrīt visas ideālās atbilstības” (apļveida bultu ikona).',
        'delete-dialog': 'Izraksta kolonnā “Options” noklikšķiniet uz “Delete bank statement” (miskastes ikona).',
        'clear-dialog':
          'Atzīmējiet izrakstu, tad ierakstā, kuram ir maksātājs un pozitīva summa, noklikšķiniet uz “Clear bank statement entry”.',
        'saldo-dialog':
          'Atzīmējiet “Iekļaut negatīvās summas”, tad ierakstā, kuram ir maksātājs un negatīva summa, noklikšķiniet uz “Clear bank statement entry”.',
        'delete-entry-dialog':
          'Manuāli ievadīta izraksta ierakstā noklikšķiniet uz “Delete bank statement entry” (miskastes ikona).',
        'info-dialog': 'Nokārtotā ierakstā noklikšķiniet uz “Clearance information” (i ikona).',
      },
      tips: [
        'Importētie izraksti ir tikai lasāmi: to šūnas nevar rediģēt un ierakstus nevar dzēst pa vienam – var tikai norādīt maksātāju. Ja fails bija kļūdains, izdzēsiet visu izrakstu (ar komentāru) un importējiet to vēlreiz.',
        'Importējot PCT atpazīst maksājumus no GrECo iekasēšanas konta, apdrošinātāju kontiem, GrECo komisijas kontiem un bankas komisijas. Izejošie maksājumi apdrošinātājiem, GrECo komisija un bankas komisija uzreiz tiek atzīmēti kā nokārtoti.',
        'Maksātāja ieteikumi secīgi pārbauda klienta numuru pirms pirmās “-” maksājuma numurā, maksātāja uzņēmuma vai personas kodu un maksātāja nosaukumu. Ja maksājuma numurs un kods norāda uz dažādiem klientiem, nekas netiek ieteikts.',
        'Apakšējā sarakstā izceltie ieraksti tika nokārtoti automātiski ar “Notīrīt visas ideālās atbilstības”.',
        'GrECo Latvia: ierakstu, kura maksājuma mērķī nosaukts importēts QTG rēķins, “Notīrīt visas ideālās atbilstības” nokārto, ja summa sakrīt ar rēķina prēmiju un katram rēķina ierakstam ir iemaksa; citādi kolonna “Gaidošs QTG rēķins” rāda rēķinu, līdz polise ir GOS un ieraksts tiek nokārtots manuāli, kur nokārtošana rēķina iemaksas rāda pirmās. QTG rēķinu kolonnas redz tikai lietotāji ar piekļuvi Latvijas GrECo uzņēmumam.',
        'Uzņēmumu izvēle augšā pa labi ir kopīga ar citām PCT lapām un nosaka, kuri izraksti tiek ielādēti.',
      ],
    },
  },
};
